export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const DEFAULT_THEME_PREFERENCE: ThemePreference = 'dark'
const THEME_COLOR_FALLBACK_LIGHT = '#f4f7fb'
const THEME_COLOR_FALLBACK_DARK = '#000000'

const normalizeThemePreference = (value: unknown): ThemePreference => {
  if (value === 'light' || value === 'dark' || value === 'system') {
    return value
  }

  return DEFAULT_THEME_PREFERENCE
}

export const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return DEFAULT_THEME_PREFERENCE === 'dark'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const resolveTheme = (
  preference: ThemePreference,
  prefersDark = getSystemPrefersDark(),
): ResolvedTheme => {
  if (preference === 'system') {
    return prefersDark ? 'dark' : 'light'
  }

  return preference
}

export const applyResolvedThemeToDom = (theme: ResolvedTheme): void => {
  if (typeof document === 'undefined') return

  const html = document.documentElement
  const isDark = theme === 'dark'

  html.classList.remove('theme-light', 'theme-dark')
  html.classList.add(isDark ? 'theme-dark' : 'theme-light')

  // Keep Tailwind's .dark compatibility during migration.
  html.classList.toggle('dark', isDark)
  if (document.body) {
    document.body.classList.toggle('dark', isDark)
  }

  const themeMetaTag = document.querySelector('meta[name="theme-color"]')
  if (themeMetaTag) {
    const companyThemeColor = window
      .getComputedStyle(html)
      .getPropertyValue('--company-browser-theme')
      .trim()
    const fallbackThemeColor = isDark ? THEME_COLOR_FALLBACK_DARK : THEME_COLOR_FALLBACK_LIGHT

    themeMetaTag.setAttribute('content', companyThemeColor || fallbackThemeColor)
  }
}

export const applyThemePreferenceToDom = (preference: ThemePreference): ResolvedTheme => {
  const resolvedTheme = resolveTheme(preference)
  applyResolvedThemeToDom(resolvedTheme)
  return resolvedTheme
}

export const readStoredThemePreference = (
  storageKey = 'theme',
  fallback: ThemePreference = DEFAULT_THEME_PREFERENCE,
): ThemePreference => {
  if (typeof window === 'undefined') return fallback
  return normalizeThemePreference(window.localStorage.getItem(storageKey) ?? fallback)
}

export const normalizeStoredThemePreference = normalizeThemePreference
